import { memo } from 'react';
import clsx from 'clsx';
import { ColorCSSVariable } from '@headless-workspace/config';
import { tailwindTheme } from '@headless-workspace/theme';
import { ComponentMemo } from '@headless-workspace/typings';
import { cva } from 'class-variance-authority';
import { PropsWithAlt, PropsWithClassName, PropsWithIcon, PropsWithAccessibilityTitle } from '../../props';

export type IconType = 'small' | 'medium' | 'large' | 'xLarge' | 'xxLarge' | 'xxxLarge' | 'fullSize';

const iconTypeToSize = (type: IconType) => {
    switch (type) {
        case 'small':
            return {
                height: tailwindTheme.minHeight.iconSmall,
                width: tailwindTheme.minWidth.iconSmall,
            };
        case 'medium':
            return {
                height: tailwindTheme.minHeight.iconMedium,
                width: tailwindTheme.minWidth.iconMedium,
            };
        case 'large':
            return {
                height: tailwindTheme.minHeight.iconLarge,
                width: tailwindTheme.minWidth.iconLarge,
            };
        case 'xLarge':
            return {
                height: tailwindTheme.minHeight.iconXLarge,
                width: tailwindTheme.minWidth.iconXLarge,
            };
        case 'xxLarge':
            return {
                height: tailwindTheme.minHeight.iconXXLarge,
                width: tailwindTheme.minWidth.iconXXLarge,
            };
        case 'xxxLarge':
            return {
                height: tailwindTheme.minHeight.iconXXXLarge,
                width: tailwindTheme.minWidth.iconXXXLarge,
            };
        default:
            return {};
    }
};

const tagStyle = cva('', {
    variants: {
        type: {
            small: 'min-h-iconSmall min-w-iconSmall stroke-[0.0625rem]',
            medium: 'min-h-iconMedium min-w-iconMedium stroke-[0.09375rem]',
            large: 'min-h-iconLarge min-w-iconLarge stroke-[0.125rem]',
            xLarge: 'min-h-iconXLarge min-w-iconXLarge stroke-[0.15625rem]',
            xxLarge: 'min-h-iconXXLarge min-w-iconXXLarge stroke-[0.25rem]',
            xxxLarge: 'min-h-iconXXXLarge min-w-iconXXXLarge stroke-[0.5rem]',
            fullSize: '',
        },
    },
    defaultVariants: {
        type: 'medium',
    },
});

export type GlowIconProps = PropsWithClassName &
    PropsWithAlt &
    PropsWithAccessibilityTitle &
    PropsWithIcon & {
        fill?: string;
        fillCSSVariable?: ColorCSSVariable;
        type?: IconType;
        height?: string | number;
        width?: string | number;
    };

/**
 * A component that renders an icon.
 * @param accessibilityTitle - The title attribute for an accessible icon.
 * @param Icon - The icon to render.
 * @param type - The size of the icon. Can be 'medium' (24x24), 'large' (32x32) or 'fullSize' (original size).
 * @param fill - The color of the icon.
 * @param fillCSSVariable - The color of the icon as a CSS variable.
 * @param height - The height of the icon. To be used when type = fullSize.
 * @param width - The width of the icon. To be used when type = fullSize.
 * @param className - Additional classes to apply.
 * @param alt - The alt text for the icon.
 */
export const GlowIcon: ComponentMemo<GlowIconProps> = memo(
    ({ accessibilityTitle, Icon, type = 'medium', fill, height, width, className, alt = '' }) => {
        const size = iconTypeToSize(type);
        const fillProp = fill ? { fill } : {};
        return (
            <Icon
                className={clsx(tagStyle({ type }), className)}
                alt={alt}
                height={height}
                width={width}
                title={accessibilityTitle}
                {...size}
                {...fillProp}
            />
        );
    },
);

GlowIcon.displayName = 'GlowIcon';
